/**
 * useBridge — derived hook for Projects ↔ Accounting cross-store data.
 *
 * Replaces the legacy `useBridgeStore` (and its apps/web clone). State was
 * 100% derived/cached (snapshots Map) or working drafts (proposals array);
 * both are now either recomputed via `useMemo` from react-query caches, or
 * held as local component state in the consumer.
 *
 * Consumers: `EconomiaSection` in `apps/web/src/views/ProjectsView.tsx`.
 *
 * Persistence: NONE. Draft proposals are intentionally not persisted —
 * they are a working UI concept. Real "saved" drafts would belong on the
 * backend once a dedicated GraphQL type is exposed.
 */

import { useCallback, useMemo, useState } from 'react';
import type {
  CentsAmount,
  Invoice,
  InvoiceDraftProposal,
  InvoiceItem,
  ProjectAccountingSnapshot,
  TimeEntry,
} from '../domain/models';
import { genId } from '../domain/idPrefixes';
import { useProjectsStore } from '../stores/projectsStore';
import { useInvoices, useCreateInvoice, useUpdateInvoice } from './useAccounting';
import {
  applyVat,
  calculateBillableRevenue,
  calculateBurnRate,
  calculateTimeEntriesCost,
  generateInvoiceNumber,
} from '../utils/economics';

// =============================================================================
// Pure selectors (no store, no React)
// =============================================================================

/**
 * All time entries for a project that have not yet been linked to an issued
 * invoice. Today the heuristic is "billable entries whose id is not in any
 * issued invoice items" — kept simple. Will be tightened once the backend
 * exposes a per-entry invoice link.
 */
export function unbilledTimeEntries(
  allEntries: TimeEntry[],
  projectInvoices: Invoice[]
): TimeEntry[] {
  const linkedEntryIds = new Set<string>();
  for (const inv of projectInvoices) {
    if (inv.direction === 'issued' && inv.status !== 'cancelled') {
      // No per-item entryId tracking yet — see comment above.
    }
  }
  void linkedEntryIds; // reserved for future use
  return allEntries.filter((te) => te.billable);
}

/**
 * Issued (non-cancelled) invoices for a project.
 */
export function issuedInvoicesForProject(invoices: Invoice[], projectId: string): Invoice[] {
  return invoices.filter(
    (i) => i.projectId === projectId && i.direction === 'issued' && i.status !== 'cancelled'
  );
}

export function allProjectInvoices(invoices: Invoice[], projectId: string): Invoice[] {
  return invoices.filter((i) => i.projectId === projectId);
}

// =============================================================================
// Hook: useBridge
// =============================================================================

export interface UseBridgeResult {
  // Snapshot (derived, recomputed via useMemo)
  snapshot: ProjectAccountingSnapshot | null;

  // Project invoices (read)
  projectInvoices: Invoice[];
  projectInvoicedTotal: CentsAmount;

  // Time entries (read)
  unbilledEntries: TimeEntry[];

  // Draft proposals (local component state)
  draftProposals: InvoiceDraftProposal[];

  // Mutations
  createDraftProposal: (timeEntryIds?: string[]) => InvoiceDraftProposal | null;
  deleteDraftProposal: (id: string) => boolean;
  convertProposalToInvoice: (
    proposalId: string,
    counterparty: string
  ) => Promise<string | null>;
  linkInvoiceToProject: (invoiceId: string, projectId: string) => Promise<void>;
  unlinkInvoiceFromProject: (invoiceId: string) => Promise<void>;
}

/**
 * Derived hook exposing the Projects ↔ Accounting cross-store data for a
 * single project, plus local state for working invoice-draft proposals.
 *
 * Draft proposals are intentionally local: there is no backend
 * `InvoiceDraftProposal` type yet. They are wiped on hard refresh, which
 * matches their "draft" semantics.
 */
export function useBridge(projectId: string): UseBridgeResult {
  // Local state for working drafts. Component-level, not shared.
  const [draftProposals, setDraftProposals] = useState<InvoiceDraftProposal[]>([]);

  // react-query hooks (live, cache-backed)
  const { data: invoices = [] } = useInvoices();
  const createInvoice = useCreateInvoice();
  const updateInvoice = useUpdateInvoice();

  // Pull synchronous project + time-entry data from the projects store.
  // This store is the canonical client cache for projects; migrating it to
  // react-query is tracked separately and out of scope here.
  const project = useProjectsStore((s) => s.getProject(projectId));
  const timeEntries = useProjectsStore((s) => s.getProjectTimeEntries(projectId));

  // Derived: project-scoped invoice subsets.
  const projectInvoices = useMemo(
    () => allProjectInvoices(invoices, projectId),
    [invoices, projectId]
  );

  const projectInvoicedTotal = useMemo(
    () =>
      issuedInvoicesForProject(invoices, projectId).reduce(
        (sum, i) => sum + i.total,
        0
      ),
    [invoices, projectId]
  );

  const unbilledEntries = useMemo(
    () => unbilledTimeEntries(timeEntries, projectInvoices),
    [timeEntries, projectInvoices]
  );

  // Derived: project accounting snapshot (replaces the cached Map entry).
  const snapshot = useMemo<ProjectAccountingSnapshot | null>(() => {
    if (!project) return null;

    const costTotal = calculateTimeEntriesCost(timeEntries);
    const revenuePotential = calculateBillableRevenue(timeEntries);
    const margin = projectInvoicedTotal - costTotal;
    const burnRate = calculateBurnRate(timeEntries);

    let forecastFinishCost: CentsAmount | undefined;
    let forecastFinishRevenue: CentsAmount | undefined;
    if (project.progress > 0 && project.progress < 100) {
      const progressFactor = 100 / project.progress;
      forecastFinishCost = Math.round(costTotal * progressFactor);
      forecastFinishRevenue = Math.round(revenuePotential * progressFactor);
    }

    return {
      id: `pas-${projectId}`, // stable id per project — no need for a fresh genId
      projectId,
      at: new Date().toISOString(),
      costTotal,
      revenuePotential,
      revenueInvoiced: projectInvoicedTotal,
      margin,
      burnRate,
      forecastFinishCost,
      forecastFinishRevenue,
    };
  }, [project, projectId, timeEntries, projectInvoicedTotal]);

  // -------------------------------------------------------------------------
  // Draft proposal mutations (local)
  // -------------------------------------------------------------------------

  const createDraftProposal = useCallback(
    (timeEntryIds?: string[]): InvoiceDraftProposal | null => {
      if (!project) return null;

      const entries =
        timeEntryIds && timeEntryIds.length > 0
          ? timeEntries.filter(
              (te) => timeEntryIds.includes(te.id) && te.billable
            )
          : unbilledEntries;

      if (entries.length === 0) return null;

      const subtotal = calculateBillableRevenue(entries);
      const { total, vatTotal } = applyVat(subtotal);

      const totalHours = entries.reduce((sum, te) => sum + te.minutes, 0) / 60;
      const notes: string[] = [
        `${entries.length} registrazioni tempo`,
        `${totalHours.toFixed(1)} ore totali`,
      ];
      if (project.clientName) {
        notes.push(`Cliente: ${project.clientName}`);
      }

      const proposal: InvoiceDraftProposal = {
        id: genId('prp'),
        projectId,
        timeEntryIds: entries.map((te) => te.id),
        total,
        vatTotal,
        suggestedNumber: generateInvoiceNumber(),
        notes,
      };

      setDraftProposals((prev) => [proposal, ...prev]);
      return proposal;
    },
    [project, projectId, timeEntries, unbilledEntries]
  );

  const deleteDraftProposal = useCallback((id: string): boolean => {
    let removed = false;
    setDraftProposals((prev) => {
      const next = prev.filter((p) => p.id !== id);
      removed = next.length !== prev.length;
      return next;
    });
    return removed;
  }, []);

  // -------------------------------------------------------------------------
  // Proposal → invoice (react-query mutation)
  // -------------------------------------------------------------------------

  const convertProposalToInvoice = useCallback(
    async (proposalId: string, counterparty: string): Promise<string | null> => {
      const proposal = draftProposals.find((p) => p.id === proposalId);
      if (!proposal || !project) return null;

      const entries = timeEntries.filter((te) =>
        proposal.timeEntryIds.includes(te.id)
      );

      // Aggregate into a single line item (matches legacy behavior).
      const totalMinutes = entries.reduce((sum, te) => sum + te.minutes, 0);
      const totalHours = totalMinutes / 60;
      const avgRate =
        entries.length > 0
          ? entries.reduce((sum, te) => sum + te.billableRate, 0) / entries.length
          : 0;

      const lineSubtotal = Math.round(totalHours * avgRate);
      const { total: lineTotal } = applyVat(lineSubtotal);

      const items: InvoiceItem[] = [
        {
          description: `Prestazioni professionali - ${project.title}`,
          quantity: parseFloat(totalHours.toFixed(2)),
          unitPrice: Math.round(avgRate),
          vatRate: 0.22,
          total: lineTotal,
        },
      ];

      const dueAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

      const created = await createInvoice.mutateAsync({
        number: proposal.suggestedNumber,
        at: new Date().toISOString(),
        dueAt,
        direction: 'issued',
        counterparty: counterparty || project.clientName || 'Cliente',
        status: 'draft',
        projectId: proposal.projectId,
        items,
      });

      // Drop the proposal locally once the invoice is persisted.
      setDraftProposals((prev) => prev.filter((p) => p.id !== proposalId));

      return created.id;
    },
    [draftProposals, project, timeEntries, createInvoice]
  );

  // -------------------------------------------------------------------------
  // Invoice ↔ project link (react-query mutation)
  // -------------------------------------------------------------------------

  const linkInvoiceToProject = useCallback(
    async (invoiceId: string, targetProjectId: string) => {
      await updateInvoice.mutateAsync({
        id: invoiceId,
        updates: { projectId: targetProjectId },
      });
    },
    [updateInvoice]
  );

  const unlinkInvoiceFromProject = useCallback(
    async (invoiceId: string) => {
      await updateInvoice.mutateAsync({
        id: invoiceId,
        updates: { projectId: undefined },
      });
    },
    [updateInvoice]
  );

  return {
    snapshot,
    projectInvoices,
    projectInvoicedTotal,
    unbilledEntries,
    draftProposals: draftProposals.filter((p) => p.projectId === projectId),
    createDraftProposal,
    deleteDraftProposal,
    convertProposalToInvoice,
    linkInvoiceToProject,
    unlinkInvoiceFromProject,
  };
}
